import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLoginInfo from "hooks/useLoginInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import { useLocation } from "react-router-dom";
import { renderStatusColor } from "utils/statusColor";
import TabSettings from "components/__new/common/TabSettings";
import Filter, { ActiveFilter } from "components/__new/common/Filter";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import { BackButtonPage } from "components/__new/common/BackButton";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import { DateFormat, TimeFormat, DateFullFormat } from "utils/dateFormat";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import FormDrawer from "components/__new/common/form-drawer";
import ModalDetailsContent from "components/__new/common/ModalDetailsContent";
import * as walletAction from "store/entities/wallet/action";


export const WalletTransactions = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const prevPage = location?.state?.from?.pathname;
    const { transactionsCountLoading, transactionsCount, transactionsLoading, transactions, transactionsDownloadLoading, transactionsDownload } = useSelector((s) => s.entities.wallet);


    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const [transaction, setTransaction] = useState({});
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    // const getTransactionsCount = () => {
    //     let params = {...listParams};

    //     params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
    //     params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

    //     dispatch(walletAction.getTransactionsCount(userInfo.token, params));
    // }


    const getTransactions = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);
        dispatch(walletAction.getTransactions(userInfo.token, params));
    }


    const displayTransactionsCount = (type) => {
        if (transactionsCountLoading === true){
            return (<FontAwesomeIcon icon="circle-notch" spin />);
        }
        else{
            if (type === "all") return ("(" + transactionsCount?.data?.all || "-") + ")";
            if (type === "successful") return ("(" + transactionsCount?.data?.successful || "-") + ")";
            if (type === "pending") return ("(" + transactionsCount?.data?.pending || "-") + ")";
            if (type === "failed") return ("(" + transactionsCount?.data?.failed || "-") + ")";
            if (type === "reversed") return ("(" + transactionsCount?.data?.reversed || "-") + ")";
        }
    }


    const viewDetails = (transaction) => {
        setTransaction(transaction);
        setIsOpen(true);
    }


    useEffect(() => {
        // getTransactionsCount();
        getTransactions();
        // eslint-disable-next-line
    }, [location]);


    return (
        <div>

            <DynamicHeadTag
                headerText="Transactions | Wallet"
            />


            <TransactionDetails
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                transaction={transaction}
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        <Maybe condition={prevPage === "/wallet" || prevPage === "/payments/account"}>
                            <BackButtonPage link="/payments/account" />
                        </Maybe>
                        Transactions
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <div className="mt-6 box">
                <div className="border-b">
                    <TabSettings options={[
                        {
                            title: <span>All {displayTransactionsCount("all")}</span>,
                            type: "query",
                            queryName: "status",
                            queryValue: "",
                            queryValueDefault: true,
                        },
                        {
                            title: <span>Successful {displayTransactionsCount("successful")}</span>,
                            type: "query",
                            queryName: "status",
                            queryValue: "successful",
                        },
                        {
                            title: <span>Pending {displayTransactionsCount("pending")}</span>,
                            type: "query",
                            queryName: "status",
                            queryValue: "pending",
                        },
                        {
                            title: <span>Failed {displayTransactionsCount("failed")}</span>,
                            type: "query",
                            queryName: "status",
                            queryValue: "failed",
                        },
                        {
                            title: <span>Reversed {displayTransactionsCount("reversed")}</span>,
                            type: "query",
                            queryName: "status",
                            queryValue: "reversed",
                        },
                    ]} />
                </div>

                <div className="mt-6 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <Filter filters={[
                                {
                                    title: "Status",
                                    name: "status",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Successful",
                                            payload: "successful",
                                        },
                                        {
                                            display: "Pending",
                                            payload: "pending",
                                        },
                                        {
                                            display: "Failed",
                                            payload: "failed",
                                        },
                                        {
                                            display: "Reversed",
                                            payload: "reversed",
                                        },
                                    ],
                                },
                                {
                                    title: "Date Range",
                                    dataType: "date-range",
                                    options: {
                                        from: {
                                            name: "startDate",
                                            placeholder: "Start Date",
                                        },
                                        to: {
                                            name: "endDate",
                                            placeholder: "End Date",
                                        },
                                    },
                                },
                            ]}/>
                        </div>

                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download transactions list"
                                filename="Transactions.csv"
                                columns={[
                                    { label: "Transaction Date", key: "createdAt" },
                                    { label: "Transaction Type", key: "type" },
                                    { label: "Source Type", key: "sourceType" },
                                    { label: "Target", key: "target" },
                                    { label: "Previous Balance", key: "previousBalance" },
                                    { label: "Amount", key: "amount" },
                                    { label: "Current Balance", key: "newBalance" },
                                    { label: "Reference", key: "reference" },
                                    { label: "Transaction Reference", key: "transactionRef" },
                                    { label: "Approval Status", key: "status" },
                                ]}
                                click={() => {
                                    dispatch(walletAction.getTransactionsDownload(userInfo.token, {...listParams, page: 1, limit: 10000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={transactionsDownloadLoading}
                                response={transactionsDownload?.data?.docs}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>

                <div>
                    <ActiveFilter filters={{"Status": "status"}} />
                </div>

                <div className="mt-6">
                    <div ref={printComponentRef}>

                        <div className="mb-4 text-xl hidden print:block">
                            Transactions
                        </div>

                        <Table>
                            <Table.Head>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Previous Balance</th>
                                <th>Details</th>
                                <th>Amount</th>
                                <th>Current Balance</th>
                                <th>Approval Status</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={transactionsLoading}
                                    loadingText="Loading transactions"
                                    data={transactions?.data}
                                    colspan={8}
                                />

                                {transactionsLoading === false && transactions?.data?.docs?.map((transaction) =>
                                    <tr key={transaction._id}>
                                        <td>
                                            <div>
                                                {DateFormat(transaction.createdAt)}
                                            </div>
                                            <div>
                                                at {TimeFormat(transaction.createdAt)}
                                            </div>
                                        </td>
                                        <td className="capitalize">
                                            {transaction.type}
                                        </td>
                                        <td>
                                            {CurrencyFormat(transaction.previousBalance / 100)}
                                        </td>
                                        <td>
                                            <div className="capitalize">
                                                {transaction?.sourceType?.replaceAll("-", " ")}
                                            </div>
                                            <div className="max-w-[200px] text-fade" style={{whiteSpace: "normal"}}>
                                                {transaction.description}
                                            </div>
                                        </td>
                                        <td>
                                            {CurrencyFormat(transaction.amount / 100)}
                                        </td>
                                        <td>
                                            {CurrencyFormat(transaction.newBalance / 100)}
                                        </td>
                                        <td>
                                            <div className={"label " + renderStatusColor(transaction.status)}>
                                                {transaction.status}
                                            </div>
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => {viewDetails(transaction)}} className="btn btn-transparent-black btn-sm">
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={transactionsLoading === false && transactions?.data?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={transactions?.data} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>

        </div>
    )
}




export const TransactionDetails = (props) => {
    const { transaction, isOpen, setIsOpen } = props;

    return (
        <FormDrawer isOpen={isOpen} setIsOpen={setIsOpen}>
            <div>
                <div className="p-6">
                    <div className="flex justify-between">
                        <div className="font-medium text-2xl">
                            Transaction Details
                        </div>
                        <div onClick={() => setIsOpen(false)} className="modal-close group">
                            <FontAwesomeIcon icon="times" className="customizeSVG group-hover:text-white m-auto" />
                        </div>
                    </div>
                    <div className="text-fade text-sm">
                        {transaction.description}
                    </div>
                </div>

                <div className="w-full h-px my-2 bg-gray-200"></div>

                <div className="p-6 flex flex-row flex-wrap">
                    {/*
                    <div className="w-full mb-6 font-semibold text-sm">
                        Legal Information
                    </div>
                     */}
                    <ModalDetailsContent label="Transaction Date" value={DateFullFormat(transaction.createdAt) + " at " + TimeFormat(transaction.createdAt)} />
                    <ModalDetailsContent label="Transaction Type" value={<div className="capitalize">{transaction.type}</div>} />
                    <ModalDetailsContent label="Source Type" value={<div className="capitalize">{transaction.sourceType?.replaceAll("-", " ")}</div>} />
                    <ModalDetailsContent label="Target" value={<div className="capitalize">{transaction.target}</div>} />
                    <ModalDetailsContent label="Previous Balance" value={CurrencyFormat(transaction.previousBalance / 100)} />
                    <ModalDetailsContent label="Reference" value={transaction.reference} />
                    <ModalDetailsContent label="Amount" value={CurrencyFormat(transaction.amount / 100)} />
                    <ModalDetailsContent label="Transaction Reference" value={transaction.transactionRef} />
                    <ModalDetailsContent label="Current Balance" value={CurrencyFormat(transaction.newBalance / 100)} />
                    <ModalDetailsContent label="Approval Status" value={<div className={"label " + renderStatusColor(transaction.status)}>{transaction.status}</div>} />
                    {transaction?.sourceType !== "charge" && <ModalDetailsContent label="Transaction Description" value={transaction?.description || "N/A"} />}

                    <Maybe condition={transaction?.sourceType === "bill-payment" && transaction?.providerResponse?.units?.length}>
                        <ModalDetailsContent label="Recharge token" value={transaction?.providerResponse?.token} />
                        <ModalDetailsContent label="Recharge amount" value={CurrencyFormat(transaction?.providerResponse?.amount)} />
                        <ModalDetailsContent label="Meter number" value={transaction?.providerResponse?.account} />
                        <ModalDetailsContent label="Units" value={transaction?.providerResponse?.units} />
                        <ModalDetailsContent label="Name" value={transaction?.providerResponse?.name} />
                        <ModalDetailsContent label="Address" value={transaction?.providerResponse?.address} />
                    </Maybe>
                    
                    
                    <Maybe condition={transaction?.sourceType === "bank-transfer" && transaction?.recipient?.bankName?.length}>
                        <ModalDetailsContent label="Recipient Bank" value={transaction?.recipient?.bankName} />
                        <ModalDetailsContent label="Recipient Account Number" value={transaction?.recipient?.accountNumber} />
                        <ModalDetailsContent label="Recipient Account Name" value={transaction?.recipient?.accountName} />
                    </Maybe>
                </div>
            </div>
        </FormDrawer>
    )
}



export default WalletTransactions;